/**
 * MKS-194 — turn `http(s)` URLs inside a PLAIN-TEXT string into anchors.
 *
 * For surfaces that are not markdown (lead notes). Comment bodies go through
 * `components/comments/MarkdownBody` instead, where remark-gfm already
 * auto-links.
 *
 * The input is user-authored and untrusted, so it is only ever emitted as React
 * children — never `dangerouslySetInnerHTML`. A note containing `<b>bold</b>`
 * renders those angle brackets literally, and only the `https?:` scheme ever
 * becomes an href (a `javascript:` URL is not matched, so it stays text).
 *
 * Scope note: the ticket asks for explicit `http(s)` URLs. A bare `www.x.com`
 * stays plain text — guessing a scheme for every dotted token turns ordinary
 * prose ("see note 3.b") into links.
 */
import { Fragment, type ReactNode } from 'react';

/**
 * Deliberately permissive after the scheme: query strings in the wild carry
 * `?`, `&`, `=`, `#`, `%` and commas. Whitespace, angle brackets and quotes end
 * the URL; `trimTrailing` then gives back the characters that were really
 * sentence punctuation.
 */
const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;

/** Sentence punctuation that follows a URL far more often than it ends one. */
const TRAILING_PUNCTUATION = '.,;:!?';

/**
 * Give back trailing characters that belong to the sentence, not the URL.
 * A `)` is only given back when it has no opener inside the match, so
 * `https://en.wikipedia.org/wiki/Monk_(disambiguation)` survives intact while
 * `(see https://example.com)` does not swallow the closing paren.
 */
function trimTrailing(url: string): string {
  let end = url.length;
  while (end > 0) {
    const char = url[end - 1]!;
    if (TRAILING_PUNCTUATION.includes(char)) {
      end -= 1;
      continue;
    }
    if (char === ')') {
      const head = url.slice(0, end);
      const opened = (head.match(/\(/g) ?? []).length;
      const closed = (head.match(/\)/g) ?? []).length;
      if (closed > opened) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return url.slice(0, end);
}

export interface LinkifyProps {
  /** Plain text, as the user typed it. */
  text: string;
}

export function Linkify({ text }: LinkifyProps) {
  if (!text) return null;

  // A fresh regex per render: `lastIndex` on a shared /g regex leaks between calls.
  const pattern = new RegExp(URL_PATTERN.source, URL_PATTERN.flags);
  const parts: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const url = trimTrailing(match[0]);
    // Nothing left after trimming (e.g. a lone `https://.`) — leave it as text.
    if (url === 'http://' || url === 'https://') continue;

    const start = match.index;
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <a
        key={`${start}-${url}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary-600 underline break-words"
      >
        {url}
      </a>,
    );
    cursor = start + url.length;
    // Re-anchor the scan after the trimmed URL so punctuation we gave back is
    // still seen as plain text.
    pattern.lastIndex = cursor;
  }

  if (cursor < text.length) parts.push(text.slice(cursor));

  return <Fragment>{parts}</Fragment>;
}

export default Linkify;
